'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { escalationListMachine } from './machines/escalationListMachine'
import { processEscalation, shouldTriggerEscalation } from './escalation-engine'
import type {
  EscalationEvent,
  EscalationPolicy,
  TicketEscalationHistory,
  EscalationRequest,
} from './types'
import type { EscalationRule } from '../../sla'

export type EscalationListMachineApi = ReturnType<typeof useMachine<typeof escalationListMachine>>

const EscalationListMachineContext = createContext<EscalationListMachineApi | null>(null)

export function EscalationListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(escalationListMachine)
  return (
    <EscalationListMachineContext.Provider value={api}>
      {children}
    </EscalationListMachineContext.Provider>
  )
}

export function useEscalationListMachine() {
  const ctx = useContext(EscalationListMachineContext)
  if (!ctx) {
    throw new Error('useEscalationListMachine must be used within EscalationListMachineProvider')
  }
  return ctx
}

export function useEscalationListMachineOptional() {
  return useContext(EscalationListMachineContext)
}

export function useEscalationListStoreCompat() {
  const machineApi = useEscalationListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context

    const getEventsByTicket = (ticketId: string) =>
      ctx.events.filter((e) => e.ticketId === ticketId)
    const getPendingEvents = () => ctx.events.filter((e) => e.status === 'pending')
    const getPolicyById = (id: string) => ctx.policies.find((p) => p.id === id)
    const getTicketHistory = (ticketId: string): TicketEscalationHistory => {
      const events = getEventsByTicket(ticketId)
      const sortedEvents = [...events].sort((a, b) => b.triggeredAt - a.triggeredAt)
      return {
        ticketId,
        ticketNumber: events[0]?.ticketNumber || '',
        events: sortedEvents,
        totalEscalations: events.length,
        lastEscalatedAt: sortedEvents[0]?.triggeredAt,
      }
    }

    const triggerEscalation = async (
      request: EscalationRequest,
      rule: EscalationRule
    ): Promise<EscalationEvent> => {
      const activePolicy = ctx.policies.find(
        (p) => p.isActive && p.slaConfigIds.includes(request.slaConfigId)
      )
      const event = await processEscalation(request, rule, activePolicy)
      send({ type: 'ADD_EVENT', event })
      return event
    }

    const checkAndTrigger = async (
      request: EscalationRequest,
      rules: EscalationRule[]
    ): Promise<EscalationEvent[]> => {
      const previousEscalations = getEventsByTicket(request.ticketId)
      const triggeredEvents: EscalationEvent[] = []
      for (const rule of rules) {
        if (shouldTriggerEscalation(request.hoursElapsed, rule, previousEscalations)) {
          const event = await triggerEscalation(request, rule)
          triggeredEvents.push(event)
          previousEscalations.push(event)
        }
      }
      return triggeredEvents
    }

    return {
      events: ctx.events,
      policies: ctx.policies,
      addEvent: (event: EscalationEvent) => send({ type: 'ADD_EVENT', event }),
      updateEvent: (id: string, updates: Partial<EscalationEvent>) =>
        send({ type: 'UPDATE_EVENT', id, updates }),
      getEventsByTicket,
      getPendingEvents,
      setPolicies: (policies: EscalationPolicy[]) => send({ type: 'SET_POLICIES', policies }),
      addPolicy: (policy: EscalationPolicy) => send({ type: 'ADD_POLICY', policy }),
      updatePolicy: (id: string, updates: Partial<EscalationPolicy>) =>
        send({ type: 'UPDATE_POLICY', id, updates }),
      deletePolicy: (id: string) => send({ type: 'DELETE_POLICY', id }),
      getPolicyById,
      triggerEscalation,
      checkAndTrigger,
      getTicketHistory,
    }
  }, [machineApi])
}

export function useEscalationActions() {
  const machineApi = useEscalationListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    addEvent: (event: EscalationEvent) => send({ type: 'ADD_EVENT', event }),
    updateEvent: (id: string, updates: Partial<EscalationEvent>) =>
      send({ type: 'UPDATE_EVENT', id, updates }),
    setPolicies: (policies: EscalationPolicy[]) => send({ type: 'SET_POLICIES', policies }),
    addPolicy: (policy: EscalationPolicy) => send({ type: 'ADD_POLICY', policy }),
    updatePolicy: (id: string, updates: Partial<EscalationPolicy>) =>
      send({ type: 'UPDATE_POLICY', id, updates }),
    deletePolicy: (id: string) => send({ type: 'DELETE_POLICY', id }),
  }), [send])
}

export function useTicketEscalations(ticketId: string) {
  const compat = useEscalationListStoreCompat()
  return useMemo(() => compat.getEventsByTicket(ticketId), [compat, ticketId])
}

export function useEscalationHistory(ticketId: string) {
  const compat = useEscalationListStoreCompat()
  return useMemo(() => compat.getTicketHistory(ticketId), [compat, ticketId])
}

export function usePendingEscalations() {
  const compat = useEscalationListStoreCompat()
  return useMemo(() => compat.getPendingEvents(), [compat])
}
